import type { ReactElement } from 'react';
import { Link, useT } from '../../../../app/di';
import { IconButton } from '../../../../ui-kit/Button/Button';
import { Checkbox } from '../../../../ui-kit/Field/Field';
import { MediaThumb, type MediaThumbSource } from '../../media';
import { viewerSearch } from '../model/post-media-viewer';
import styles from './PostMediaStrip.module.scss';

export interface PostMediaStripItem {
  mediaId: string;
  /** Сведения для превью, если уже загружены. */
  media: MediaThumbSource | undefined;
  /** POST-UC-14: медиа идёт в публикацию; false — запасное. */
  publish: boolean;
}

export interface PostMediaStripProps {
  blogId: string;
  postId: string;
  /** Медиа поста по порядку. */
  items: readonly PostMediaStripItem[];
  editable: boolean;
  pending: boolean;
  onMove(index: number, delta: number): void;
  onRemove(mediaId: string): void;
  onTogglePublish(mediaId: string, publish: boolean): void;
}

/**
 * POST-UC-3, POST-UC-3.CC-1: медиа поста по порядку публикации; порядок меняется кнопками. Клик по медиа
 * открывает просмотр (POST-UC-13). POST-UC-14: над медиа — счётчик выбранных к публикации,
 * невыбранные бледные; отметка — флажок у номера.
 */
export function PostMediaStrip({ blogId, postId, items, editable, pending, onMove, onRemove, onTogglePublish }: PostMediaStripProps): ReactElement {
  const { t } = useT();
  if (items.length === 0) return <p className="u-muted">{t('posts.media.empty')}</p>;
  const selected = items.filter((item) => item.publish).length;
  return (
    <div className="u-stack">
      <p className={styles.summary} data-testid="post-media-selected">
        {t('posts.media.selected', { n: selected, total: items.length })}
      </p>
      <ol className={styles.strip} data-testid="post-media">
        {items.map(({ mediaId, media, publish }, index) => (
          <li key={mediaId} className={publish ? styles.item : `${styles.item} ${styles.excluded}`} data-publish={publish}>
            <Link to="post" params={{ blogId, postId }} search={viewerSearch(mediaId)} keepScroll className={styles.preview}>
              {media ? <MediaThumb media={media} square /> : <div className={styles.placeholder} />}
            </Link>
            <div className={styles.controls}>
              {editable ? (
                <Checkbox
                  label={<span className="u-small">{t('posts.media.position', { n: index + 1 })}</span>}
                  aria-label={t('posts.media.publishOf', { n: index + 1 })}
                  checked={publish}
                  disabled={pending}
                  onChange={(event) => onTogglePublish(mediaId, event.target.checked)}
                />
              ) : (
                <span className="u-small u-muted">{t('posts.media.position', { n: index + 1 })}</span>
              )}
              {editable ? (
                <span className="u-row-nowrap">
                  <IconButton size="sm" icon="left" label={t('posts.media.left')} disabled={index === 0 || pending} onClick={() => onMove(index, -1)} />
                  <IconButton size="sm" icon="right" label={t('posts.media.right')} disabled={index === items.length - 1 || pending} onClick={() => onMove(index, 1)} />
                  <IconButton size="sm" icon="close" label={t('posts.media.remove')} disabled={pending} onClick={() => onRemove(mediaId)} />
                </span>
              ) : null}
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}
